import React from "react";
import {useForm, SubmitHandler} from "react-hook-form";
import {toast} from "react-toastify";
import {useNavigate, Link as RouterLink} from "react-router-dom";

import {useAuth} from "@/context/AuthContext";
import {extractFirebaseErrorCode, firebaseErrorMessages} from "@/utils/firebaseErrorMessages";

import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {Separator} from "@/components/ui/separator";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import {
    Loader2,
    User,
    Mail,
    Lock,
    CalendarDays,
    Users,
    Sparkles,
} from "lucide-react";

import logo from '@/assets/icons/kurut-logo.png'

interface IFormInputs {
    email: string;
    password: string;
    displayName: string;
    gender: string;
    birthDate: string;
    age: number;
}

const SignUp: React.FC = () => {
    const {register: registerUser, registerLoading} = useAuth();
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: {errors},
    } = useForm<IFormInputs>({
        mode: "onSubmit",
        defaultValues: {
            gender: "",
            birthDate: "",
        },
    });

    const gender = watch("gender");

    const onSubmit: SubmitHandler<IFormInputs> = async ({
                                                            email,
                                                            password,
                                                            displayName,
                                                            gender,
                                                            birthDate,
                                                        }) => {
        try {
            await registerUser(email, password, displayName, gender, birthDate);

            toast.success(
                "Аккаунт создан! Письмо с подтверждением отправлено на ваш email. Пожалуйста, подтвердите почту, прежде чем заходить."
            );

            setTimeout(() => {
                navigate("/signin");
            }, 2000);
        } catch (error: any) {
            const errorCode = extractFirebaseErrorCode(error.message);
            toast.error(
                firebaseErrorMessages[errorCode || ""] || "Произошла ошибка при регистрации"
            );
        }
    };

    return (
        <div className="mx-auto w-full max-w-md px-4 py-10 animate-fade-in">
            {/* Brand */}
            <div className="mb-6 flex flex-col items-center gap-3">
                <div
                    className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border bg-muted/20 shadow-sm">
                    <img
                        src={logo}
                        alt="KurutPhone"
                        className="h-10 w-10 object-contain"
                        draggable={false}
                    />
                </div>
                <div className="text-center">
                    <div className="text-2xl font-semibold tracking-tight">KurutPhone</div>
                    <div className="text-sm text-muted-foreground">Создание аккаунта</div>
                </div>
            </div>

            <Card className="overflow-hidden">
                <CardHeader className="space-y-1 bg-muted/20">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <div
                            className="inline-flex h-8 w-8 items-center justify-center rounded-xl border bg-background">
                            <Sparkles className="h-4 w-4 text-muted-foreground"/>
                        </div>
                        Регистрация
                    </CardTitle>
                </CardHeader>

                <CardContent className="p-5 space-y-5">
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        {/* displayName */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Имя и фамилия</Label>
                            <div className="relative">
                                <User
                                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                                <Input
                                    className={[
                                        "pl-9",
                                        errors.displayName
                                            ? "border-destructive focus-visible:ring-destructive"
                                            : "",
                                    ].join(" ")}
                                    placeholder="Напр. Айбек Асанов"
                                    autoComplete="name"
                                    {...register("displayName", {
                                        required: "Введите ваше имя и фамилию",
                                    })}
                                />
                            </div>
                            {errors.displayName?.message ? (
                                <p className="text-xs text-destructive">{errors.displayName.message}</p>
                            ) : null}
                        </div>

                        {/* email */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Email</Label>
                            <div className="relative">
                                <Mail
                                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                                <Input
                                    className={[
                                        "pl-9",
                                        errors.email ? "border-destructive focus-visible:ring-destructive" : "",
                                    ].join(" ")}
                                    placeholder="example@mail.com"
                                    inputMode="email"
                                    autoComplete="email"
                                    {...register("email", {
                                        required: "Введите адрес электронной почты",
                                        pattern: {
                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message: "Некорректный формат email",
                                        },
                                    })}
                                />
                            </div>
                            {errors.email?.message ? (
                                <p className="text-xs text-destructive">{errors.email.message}</p>
                            ) : null}
                        </div>

                        {/* password */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Пароль</Label>
                            <div className="relative">
                                <Lock
                                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                                <Input
                                    type="password"
                                    className={[
                                        "pl-9",
                                        errors.password
                                            ? "border-destructive focus-visible:ring-destructive"
                                            : "",
                                    ].join(" ")}
                                    placeholder="Минимум 6 символов"
                                    autoComplete="new-password"
                                    {...register("password", {
                                        required: "Введите пароль",
                                        minLength: {
                                            value: 6,
                                            message: "Пароль должен быть не короче 6 символов",
                                        },
                                    })}
                                />
                            </div>
                            {errors.password?.message ? (
                                <p className="text-xs text-destructive">{errors.password.message}</p>
                            ) : null}
                        </div>

                        {/* gender */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Пол</Label>

                            <div className="relative">
                                <Users
                                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>

                                <Select
                                    value={gender || ""}
                                    onValueChange={(v) => setValue("gender", v, {shouldValidate: true})}
                                >
                                    <SelectTrigger
                                        className={[
                                            "pl-9",
                                            errors.gender ? "border-destructive focus-visible:ring-destructive" : "",
                                        ].join(" ")}
                                    >
                                        <SelectValue placeholder="Выберите пол"/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Мужской">Мужской</SelectItem>
                                        <SelectItem value="Женский">Женский</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* RHF validation for Select */}
                            <input
                                type="hidden"
                                {...register("gender", {required: "Выберите пол"})}
                            />

                            {errors.gender?.message ? (
                                <p className="text-xs text-destructive">{errors.gender.message}</p>
                            ) : null}
                        </div>

                        {/* birthDate */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Дата рождения</Label>
                            <div className="relative">
                                <CalendarDays
                                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                                <Input
                                    type="date"
                                    className={[
                                        "pl-9",
                                        errors.birthDate
                                            ? "border-destructive focus-visible:ring-destructive"
                                            : "",
                                    ].join(" ")}
                                    {...register("birthDate", {required: "Введите дату рождения"})}
                                />
                            </div>
                            {errors.birthDate?.message ? (
                                <p className="text-xs text-destructive">{errors.birthDate.message}</p>
                            ) : null}
                        </div>

                        {/* submit */}
                        <Button type="submit" className="w-full" disabled={registerLoading}>
                            {registerLoading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                            ) : null}
                            Зарегистрироваться
                        </Button>
                    </form>

                    <div className="space-y-3">
                        <Separator/>

                        <div className="text-center text-sm text-muted-foreground">
                            Уже есть аккаунт?{" "}
                            <Button asChild variant="link" className="h-auto p-0 text-sm font-medium">
                                <RouterLink to="/signin">Войти</RouterLink>
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SignUp;
